import { WaypointType } from 'interfaces/waypoint'

export function get({ params }) {
  return {
    url: `/api/deliveries/${params.id}`,
    method: 'GET',
    headers: {
      Accept: 'application/ld+json',
      Authorization: null,
    },
  }
}

export function operate(params) {
  const formatParams = (data) => {
    return Object.keys(data)
      .filter((key) => {
        return !!data[key]
      })
      .filter((key) => {
        return !key.includes('notification')
      })
      .map((key) => {
        return 'operatorState[]=' + key
      })
      .join('&')
  }

  const formatSearchFilters = (filters) => {
    if (!filters) return ''

    const searchParams = []
    if (filters.orderGiverName) {
      searchParams.push(`orderGiverName=${encodeURIComponent(filters.orderGiverName)}`)
    }
    if (filters.courierName) {
      searchParams.push(`courierName=${encodeURIComponent(filters.courierName)}`)
    }
    if (filters.reference) {
      searchParams.push(`reference=${encodeURIComponent(filters.reference)}`)
    }
    if (filters.comments) {
      searchParams.push(`comments=${encodeURIComponent(filters.comments)}`)
    }

    return searchParams.join('&')
  }

  const formattedParams = formatParams(params.payload)
  const searchFilters = formatSearchFilters(params.searchFilters)

  // Ensure operatorState are always included, even with search filters
  const allParams = [formattedParams, searchFilters].filter(Boolean).join('&')

  // If we have search filters but no operatorState, we should still include operatorState
  if (params.searchFilters && (!params.payload || !formattedParams)) {
    console.warn('Search filters provided but no operatorState found. This might cause issues.')
  }

  return {
    url: `/api/deliveries/operate${allParams ? `?${allParams}` : ''}`,
    method: 'GET',
    headers: {
      Accept: 'application/ld+json',
      Authorization: null,
    },
  }
}

export function applyCourierTransition(deliveryId, transition, payload) {
  const replacedCourier = {
    courierStart: `/api/company_couriers/${payload.courierStart}`,
    courierFinish: `/api/company_couriers/${payload.courierFinish}`,
  }

  const emptyReturnRide = payload.emptyReturn ? `/api/rides/${payload.emptyReturn.value}` : null

  return {
    url: `/api/deliveries/${deliveryId}/transition`,
    method: 'PUT',
    headers: {
      Accept: 'application/ld+json',
      Authorization: null,
    },
    body: JSON.stringify({ ...replacedCourier, transition: transition, emptyReturnRide: emptyReturnRide }),
  }
}

export function operatorCourierTransition(deliveryId, transition) {
  return {
    url: `/api/deliveries/${deliveryId}/transition`,
    method: 'PUT',
    headers: {
      Accept: 'application/ld+json',
      Authorization: null,
    },
    body: JSON.stringify({ transition }),
  }
}

export function operatorTransition(deliveryId, transition, email = null, sendEmail = true) {
  return {
    url: `/api/deliveries/operate/${deliveryId}/transition`,
    method: 'PUT',
    headers: {
      Accept: 'application/ld+json',
      Authorization: null,
    },
    body: JSON.stringify({ transition, email_body: email, send_email: sendEmail }),
  }
}

export function all(params) {
  const statuses = params.statuses
    .map((key) => {
      return 'operatorState[]=' + key
    })
    .join('&')
  return {
    url: `/api/deliveries/user${
      params ? `?${statuses}&page=${params.pageNumber}&itemsPerPage=${params.itemsPerPage}&notStates=canceled` : ''
    }`,
    method: 'GET',
    headers: {
      Accept: 'application/ld+json',
      Authorization: null,
    },
  }
}

export function profitability(params) {
  return {
    url: `/api/simulated_deliveries?${params.startDate ? `startDate[after]=${params.startDate}&` : ''}${
      params.endDate ? `endDate[before]=${params.endDate}&` : ''
    }${
      params.licenseNumbers && params.licenseNumbers.length
        ? params.licenseNumbers.map((ln) => `vehicle.licenseNumber[]=${ln.value}&`).join('')
        : ''
    }`,
    method: 'GET',
    headers: {
      Accept: 'application/ld+json',
      Authorization: null,
    },
  }
}

export function reference(params) {
  return {
    url: `/api/deliveries/user?reference=${params.reference}&operatorState[]=billed&operatorState[]=billable`,
    method: 'GET',
    headers: {
      Accept: 'application/ld+json',
      Authorization: null,
    },
  }
}

export function estimate({ payload }) {
  return {
    url: `/api/deliveries/estimate`,
    method: 'POST',
    headers: {
      Accept: 'application/ld+json',
      'Content-Type': 'application/json',
      Authorization: null,
    },
    body: JSON.stringify(payload),
  }
}

export function create({ payload }) {
  return {
    url: `/api/deliveries`,
    method: 'POST',
    headers: {
      Accept: 'application/ld+json',
      'Content-Type': 'application/json',
      Authorization: null,
    },
    body: JSON.stringify(payload),
  }
}

export function createProfitability({ payload }) {
  return {
    url: `/api/simulated_deliveries`,
    method: 'POST',
    headers: {
      Accept: 'application/ld+json',
      'Content-Type': 'application/json',
      Authorization: null,
    },
    body: JSON.stringify(payload),
  }
}

export function estimateFromTemplate({ payload }) {
  return {
    url: `/api/deliveries/from_template/estimate`,
    method: 'POST',
    headers: {
      Accept: 'application/ld+json',
      'Content-Type': 'application/json',
      Authorization: null,
    },
    body: JSON.stringify(payload),
  }
}

export function createFromTemplate({ payload }) {
  return {
    url: `/api/deliveries/from_template`,
    method: 'POST',
    headers: {
      Accept: 'application/ld+json',
      'Content-Type': 'application/json',
      Authorization: null,
    },
    body: JSON.stringify(payload),
  }
}

export function transition({ params, payload }) {
  return {
    url: `/api/deliveries/${params.id}/transition`,
    method: 'PUT',
    headers: {
      Accept: 'application/ld+json',
      'Content-Type': 'application/json',
      Authorization: null,
    },
    body: JSON.stringify(payload),
  }
}

export function events({ params, payload }) {
  return {
    url: `/api/deliveries/${params.id}/events`,
    method: 'GET',
    headers: {
      Accept: 'application/ld+json',
      'Content-Type': 'application/json',
      Authorization: null,
    },
    body: JSON.stringify(payload),
  }
}

export function scheduled(params) {
  const paramsScheduled = { ...params, state: 'scheduled' }
  const formatedParams = !params
    ? 'state=scheduled&page=1'
    : Object.keys(paramsScheduled)
        .filter((key) => {
          return !!paramsScheduled[key]
        })
        .map((key) => {
          if (['t1', 't2', 't3'].includes(key)) {
            return `vehicleType[]=${key}`
          }
          if (['today', 'tomorrow', 'after_tomorrow'].includes(key)) {
            return `pickupWaypointsFromDatetime[]=${key}`
          }
          return `${key}=${paramsScheduled[key]}`
        })
        .join('&')
  return {
    url: `/api/deliveries?${formatedParams}`, // &page=${params.pageId}
    method: 'GET',
    headers: {
      Accept: 'application/ld+json',
      Authorization: null,
    },
  }
}

export function find(params) {
  return {
    url: `/api/deliveries?state=scheduled&${params}`,
    method: 'GET',
    headers: {
      Accept: 'application/ld+json',
      Authorization: null,
    },
  }
}

export function search() {
  return {
    url: '/api/deliveries/search',
    method: 'GET',
    headers: {
      Accept: 'application/ld+json',
      Authorization: null,
    },
  }
}

export function modify({ params, payload }) {
  return {
    url: `/api/deliveries/${params.id}`,
    method: 'PATCH',
    headers: {
      Accept: 'application/ld+json',
      'Content-Type': 'application/merge-patch+json',
      Authorization: null,
    },
    body: JSON.stringify(payload),
  }
}

export function updateProfitability({ payload }) {
  return {
    url: `/api/simulated_deliveries/${payload.id}`,
    method: 'PATCH',
    headers: {
      Accept: 'application/ld+json',
      'Content-Type': 'application/merge-patch+json',
      Authorization: null,
    },
    body: JSON.stringify(payload),
  }
}

export function deleteProfitability({ payload }) {
  return {
    url: `/api/simulated_deliveries/${payload.id}`,
    method: 'DELETE',
    headers: {
      Accept: 'application/ld+json',
      'Content-Type': 'application/merge-patch+json',
      Authorization: null,
    },
  }
}

export function split({ params, payload }) {
  return {
    url: `/api/deliveries/${params.id}/split`,
    method: 'POST',
    headers: {
      Accept: 'application/ld+json',
      'Content-Type': 'application/merge-patch+json',
      Authorization: null,
    },
    body: JSON.stringify(payload),
  }
}

export function deliveriesRides({ params }) {
  return {
    url: `/api/deliveries/${params.id}/rides`,
    method: 'GET',
    headers: {
      Accept: 'application/ld+json',
      Authorization: null,
    },
  }
}

export function deliveriesEmptyReturns({ params }) {
  const queryParams = params.companyId ? `?companyId=${params.companyId}` : ''
  return {
    url: `/api/deliveries/${params.id}/empty-returns${queryParams}`,
    method: 'GET',
    headers: {
      Accept: 'application/ld+json',
      Authorization: null,
    },
  }
}

export function refreshRides({ params }) {
  return {
    url: `/api/deliveries/${params.id}/rides/refresh`,
    method: 'POST',
    headers: {
      Accept: 'application/ld+json',
      'Content-Type': 'application/json',
      Authorization: null,
    },
  }
}

export function cmr({ params }) {
  return {
    url: `/api/deliveries/${params.id}/cmr`,
    method: 'GET',
    headers: {
      Accept: 'application/ld+json',
      Authorization: null,
    },
  }
}

export function deleteCmr({ params }) {
  return {
    url: `/api/deliveries/${params.id}/cmr`,
    method: 'DELETE',
    headers: {
      Accept: 'application/ld+json',
      'Content-Type': 'application/json',
      Authorization: null,
    },
  }
}

export function replaceCmr({ params }) {
  return {
    url: `/api/deliveries/${params.id}/cmr`,
    method: 'PATCH',
    headers: {
      Accept: 'application/ld+json',
      'Content-Type': 'application/merge-patch+json',
      Authorization: null,
    },
    body: JSON.stringify(params.body),
  }
}

export function validation({ params, payload }) {
  return {
    url: `/api/deliveries/${params.id}/validation`,
    method: 'PATCH',
    headers: {
      Accept: 'application/ld+json',
      'Content-Type': 'application/merge-patch+json',
      Authorization: null,
    },
    body: JSON.stringify(payload),
  }
}

export function deleteSegment(id) {
  return {
    url: `/api/deliveries/${id}/merge`,
    method: 'POST',
    headers: {
      Accept: 'application/ld+json',
      'Content-Type': 'application/json',
      Authorization: null,
    },
  }
}

export function calculateRoutingETA(id: number) {
  return {
    url: `/api/deliveries/${id}/calculate/routing_eta`,
    method: 'PATCH',
    headers: {
      Accept: 'application/ld+json',
      'Content-Type': 'application/merge-patch+json',
      Authorization: null,
    },
    body: JSON.stringify([]),
  }
}

export type EstimatedArrivalTimePayload = { id: number; waypointType: WaypointType; estimatedTimeOfArrival: Date }

export function updateEstimatedArrivalTime(payload: EstimatedArrivalTimePayload) {
  return {
    url: `/api/deliveries/update_waypoint_eta?deliveryId=${payload.id}`,
    method: 'POST',
    headers: {
      Accept: 'application/ld+json',
      'Content-Type': 'application/json',
      Authorization: null,
    },
    body: JSON.stringify({
      waypointType: payload.waypointType,
      estimatedTimeOfArrival: payload.estimatedTimeOfArrival,
    }),
  }
}

export function billableToBilled() {
  return {
    url: `/api/deliveries/transition/billable_to_billed`,
    method: 'GET',
    headers: {
      Accept: 'application/ld+json',
      'Content-Type': 'application/json',
      Authorization: null,
    },
  }
}
